class CustomPriceUpdater {
  constructor() {
    this.init();
    this.currentVariantId = null;
    this.currentSellingPlanId = null;
    this.debounceTimer = null;
    this.isUpdatingURL = false;
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.setupURLChangeListener();

    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updatePrices(e.detail.variant);
      }
    });

    this.setupSubscriptionListeners();

    document.addEventListener('submit', (e) => {
      if (e.target.id === 'product-form-product-template-main') {
        this.handleFormSubmission(e);
      }
    });

    this.setupPageVisibilityListener();
  }

  setupPageVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.reinitializeAfterTabSwitch();
      }
    });

    window.addEventListener('focus', () => {
      setTimeout(() => {
        this.checkAndReinitialize();
      }, 100);
    });
  }

  reinitializeAfterTabSwitch() {
    this.setupSubscriptionListeners();

    const currentVariantId = this.getVariantIdFromURL();
    if (currentVariantId) {
      this.updatePricesFromVariantId(currentVariantId);
    }
  }

  checkAndReinitialize() {
    const testVariantId = this.getVariantIdFromURL();
    if (testVariantId) {
      if (!this.isEventListenerWorking()) {
        this.reinitializeAfterTabSwitch();
      }
    }
  }

  isEventListenerWorking() {
    const testElement = document.querySelector('.custom-subscription-wrapper');
    if (!testElement) return false;

    const testEvent = new CustomEvent('test-variant-change');
    let eventReceived = false;

    const testListener = () => {
      eventReceived = true;
    };

    testElement.addEventListener('test-variant-change', testListener);
    testElement.dispatchEvent(testEvent);
    testElement.removeEventListener('test-variant-change', testListener);

    return eventReceived;
  }

  setupURLChangeListener() {
    let lastVariantId = this.getVariantIdFromURL();

    setInterval(() => {
      const currentVariantId = this.getVariantIdFromURL();

      if (currentVariantId && currentVariantId !== lastVariantId) {
        lastVariantId = currentVariantId;
        this.updatePricesFromVariantId(currentVariantId);
      }
    }, 100);
  }

  getVariantIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  updatePricesFromVariantId(variantId) {
    const productJson = document.getElementById('ProductJson-' + this.getSectionId());
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        const variant = product.variants.find((v) => v.id.toString() === variantId);

        if (variant) {
          this.updatePrices(variant);
          this.preserveCurrentSelection();
        }
      } catch (error) {
        console.error('Error updating prices:', error);
      }
    }
  }

  updatePrices(variant) {
    const subscriptionWrapper = document.querySelector('.custom-subscription-wrapper');
    if (!subscriptionWrapper) {
      return;
    }
    this.updatePricesInElement(subscriptionWrapper, variant);
  }

  updateSubscriptionPrices(display, variantData) {
    const subscribePriceAmount = display.querySelector('.subscribe-price-amount');
    const originalPrice = display.querySelector('.subscribe-price-details small:first-child');
    const perDeliveryPrice = display.querySelector('.subscribe-price-details small:nth-child(2)');
    const saveInfo = display.querySelector('.subscribe-price-details small:last-child');

    if (subscribePriceAmount && originalPrice && perDeliveryPrice && saveInfo) {
      const sellingPlanAllocation = this.findSellingPlanAllocation(variantData);

      if (sellingPlanAllocation) {
        subscribePriceAmount.textContent = this.formatMoney(sellingPlanAllocation.price);

        const originalPriceValue = variantData.compare_at_price || variantData.price;
        originalPrice.textContent = `Original: ${this.formatMoney(originalPriceValue)}`;

        perDeliveryPrice.textContent = `Per Delivery: ${this.formatMoney(sellingPlanAllocation.per_delivery_price)}`;

        const discountAmount = originalPriceValue - sellingPlanAllocation.per_delivery_price;
        const discountPercentage = Math.round((discountAmount / originalPriceValue) * 100);
        saveInfo.textContent = `Save: ${discountPercentage}% (${this.formatMoney(discountAmount)})`;
      }
    }
  }

  findSellingPlanAllocation(variantData) {
    const productJson = document.getElementById('ProductJson-' + this.getSectionId());
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        const variant = product.variants.find((v) => v.id === variantData.id);
        if (variant && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
          return variant.selling_plan_allocations[0];
        }
      } catch (e) {
        console.warn('Could not parse product JSON:', e);
      }
    }
    return null;
  }

  setupVariantChangeListener() {
    const variantSelects = document.querySelectorAll('variant-selects');

    variantSelects.forEach((select) => {
      select.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.updatePrices(e.detail.variant);
        }
      });
    });

    document.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.target.name && e.target.name.includes('-')) {
        this.handleRadioButtonChange(e.target);
      }
    });
  }

  handleRadioButtonChange(radioButton) {
    const variantSelects = radioButton.closest('variant-selects');
    if (variantSelects) {
      const selectedVariantData = variantSelects.querySelector('[data-selected-variant]');
      if (selectedVariantData) {
        try {
          const variant = JSON.parse(selectedVariantData.textContent);
          this.updatePrices(variant);
        } catch (e) {
          console.warn('Could not parse variant data from radio button:', e);
        }
      }
    }
  }

  setupSubscriptionListeners() {
    const subscribeRadio = document.querySelector('input[name="selling_plan"][value="subscription"]');
    const oneTimeRadio = document.querySelector('input[name="selling_plan"][value=""]');
    const frequencySelect = document.getElementById('custom_selling_plan_select');

    if (subscribeRadio) {
      subscribeRadio.removeEventListener('change', this.handleSellingPlanChange);
      subscribeRadio.addEventListener('change', (e) => {
        this.handleSellingPlanChange(e.target);
      });
    }

    if (oneTimeRadio) {
      oneTimeRadio.removeEventListener('change', this.handleSellingPlanChange);
      oneTimeRadio.addEventListener('change', (e) => {
        this.handleSellingPlanChange(e.target);
      });
    }

    if (frequencySelect) {
      frequencySelect.removeEventListener('change', this.handleFrequencyChange);
      frequencySelect.addEventListener('change', (e) => {
        this.handleFrequencyChange(e.target);
      });
    }
  }

  setupFormSubmissionListener() {
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('form')) {
        this.handleFormSubmission(e);
      }
    });
  }

  handleFormSubmission(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    const pricingTypeInput = document.querySelector('.pricing-type-input');
    const sellingPlanInput = document.querySelector('.selling-plan-input');

    if (pricingTypeInput && sellingPlanInput) {
      const pricingType = pricingTypeInput.value;
      const sellingPlan = sellingPlanInput.value;

      if (pricingType === 'subscription' && sellingPlan) {
        formData.set('selling_plan', sellingPlan);

        formData.delete('pricing_type');
        formData.delete('final_price');
      } else {
        formData.delete('selling_plan');

        formData.delete('pricing_type');
        formData.delete('final_price');
      }

      this.addToCartWithData(formData);
    } else {
      form.submit();
    }
  }

  addToCartWithData(formData) {
    const cartData = {};
    for (let [key, value] of formData.entries()) {
      cartData[key] = value;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
      .then((response) => response.json())
      .then((data) => {
        document.dispatchEvent(new CustomEvent('cart:open'));

        if (window.PUB_SUB_EVENTS) {
          window.PUB_SUB_EVENTS.publish('cart:refresh');
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      });
  }

  handleSellingPlanChange(radioButton) {
    const isSubscription = radioButton.value === 'subscription';

    const pricingTypeInput = document.querySelector('.pricing-type-input');
    const finalPriceInput = document.querySelector('.final-price-input');
    const sellingPlanInput = document.querySelector('.selling-plan-input');

    if (pricingTypeInput && finalPriceInput && sellingPlanInput) {
      if (isSubscription) {
        pricingTypeInput.value = 'subscription';
        const sellingPlanId = this.findSellingPlanId();
        if (sellingPlanId) {
          sellingPlanInput.value = sellingPlanId;
        }
        this.updateURLWithSellingPlan('subscription');
        this.updateVisualStates(radioButton);
      } else {
        pricingTypeInput.value = 'onetime';
        sellingPlanInput.value = '';
        this.updateURLWithSellingPlan('onetime');
        this.updateVisualStates(radioButton);
      }
    }
  }

  findSellingPlanId() {
    const frequencySelect = document.getElementById('custom_selling_plan_select');
    if (!frequencySelect) {
      return null;
    }

    const selectedIndex = frequencySelect.selectedIndex;

    const productJson = document.getElementById('ProductJson-' + this.getSectionId());
    if (!productJson) {
      return null;
    }

    try {
      const product = JSON.parse(productJson.textContent);

      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (!variantId) {
        return null;
      }

      const currentVariant = product.variants.find((v) => v.id.toString() === variantId);

      if (!currentVariant) {
        return null;
      }

      if (currentVariant.selling_plan_allocations && currentVariant.selling_plan_allocations.length > 0) {
        const selectedAllocation = currentVariant.selling_plan_allocations[selectedIndex];
        if (selectedAllocation) {
          if (selectedAllocation.selling_plan_id) {
            return selectedAllocation.selling_plan_id;
          }
        }
      }
    } catch (error) {
      console.error('Error finding selling plan ID:', error);
    }

    return null;
  }

  updateURLWithSellingPlan(pricingType) {
    return;
  }

  handleFrequencyChange(select) {
    const sellingPlanInput = document.querySelector('.selling-plan-input');
    if (sellingPlanInput) {
      const sellingPlanId = this.findSellingPlanId();
      if (sellingPlanId) {
        sellingPlanInput.value = sellingPlanId;
      }
    }

    this.updateDaysTextFromSelect(select);
  }

  updateVisualStates(selectedRadio) {
    const subscriptionWrapper = document.querySelector('.custom-subscription-wrapper');
    if (!subscriptionWrapper) return;

    const allOptions = subscriptionWrapper.querySelectorAll('.custom-subscription-wrapper-option');

    allOptions.forEach((option) => {
      option.classList.remove('active');
    });

    const selectedOption = selectedRadio.closest('.custom-subscription-wrapper-option');
    if (selectedOption) {
      selectedOption.classList.add('active');
    }
  }

  updateFinalPrice() {
    const pricingTypeInput = document.querySelector('.pricing-type-input');
    const finalPriceInput = document.querySelector('.final-price-input');

    if (!pricingTypeInput || !finalPriceInput) return;

    if (pricingTypeInput.value === 'subscription') {
      const frequencySelect = document.getElementById('custom_selling_plan_select');
      if (frequencySelect && frequencySelect.selectedOptions[0]) {
        const selectedOption = frequencySelect.selectedOptions[0];
        const perDeliveryPrice = parseInt(selectedOption.dataset.perDelivery);
        finalPriceInput.value = perDeliveryPrice;
      }
    } else {
      const priceAmount = document.querySelector('.price-amount .money');
      if (priceAmount) {
        const priceText = priceAmount.textContent.replace('$', '').replace(',', '');
        const priceCents = Math.round(parseFloat(priceText) * 100);
        finalPriceInput.value = priceCents;
      }
    }
  }

  updatePricesForFrequency(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    if (!selectedOption) return;

    const price = parseInt(selectedOption.dataset.price);
    const perDelivery = parseInt(selectedOption.dataset.perDelivery);

    const subscribePriceAmount = document.querySelector('.custom-subscription-amount .money');
    if (subscribePriceAmount) {
      subscribePriceAmount.textContent = this.formatMoney(price);
    }

    const daysText = selectElement.options[selectElement.selectedIndex].text;
    const daysElement = document.querySelector('.days');
    if (daysElement) {
      daysElement.textContent = daysText.toLowerCase();
    }

    const refillText = document.querySelector('.custom-subscribe-option-texts li:first-child');
    if (refillText) {
      refillText.innerHTML = `${this.formatMoney(
        perDelivery
      )} refill every <span class="days">${daysText.toLowerCase()}</span>`;
    }
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  getSectionId() {
    const productInfo = document.querySelector('product-info');
    return productInfo ? productInfo.dataset.section : null;
  }

  updateUIForSellingPlan(sellingPlanId) {
    const subscribeRadio = document.getElementById('custom_selling_plan_subscribe');
    if (subscribeRadio) {
      subscribeRadio.checked = true;
      subscribeRadio.dispatchEvent(new Event('change'));
    }

    const frequencySelect = document.getElementById('custom_selling_plan_select');
    if (frequencySelect) {
      for (let option of frequencySelect.options) {
        if (option.value === sellingPlanId) {
          frequencySelect.value = sellingPlanId;
          frequencySelect.dispatchEvent(new Event('change'));
          break;
        }
      }
    }
  }

  updateUIForOneTime() {
    const oneTimeRadio = document.getElementById('custom_selling_plan_onetime');
    if (oneTimeRadio) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change'));
    }
  }

  preserveCurrentSelection() {
    const subscribeRadio = document.querySelector('input[name="selling_plan"][value="subscription"]');
    const oneTimeRadio = document.querySelector('input[name="selling_plan"][value=""]');

    if (subscribeRadio && subscribeRadio.checked) {
      this.updateSellingPlanForCurrentVariant();
      this.updateVisualStates(subscribeRadio);
    } else if (oneTimeRadio && oneTimeRadio.checked) {
      this.updateVisualStates(oneTimeRadio);
    } else {
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        this.updateVisualStates(oneTimeRadio);
      }
    }
  }

  updateSellingPlanForCurrentVariant() {
    const sellingPlanInput = document.querySelector('.selling-plan-input');
    const pricingTypeInput = document.querySelector('.pricing-type-input');

    if (sellingPlanInput && pricingTypeInput) {
      const newSellingPlanId = this.findSellingPlanId();

      if (newSellingPlanId) {
        sellingPlanInput.value = newSellingPlanId;
        pricingTypeInput.value = 'subscription';
      } else {
        sellingPlanInput.value = '';
        pricingTypeInput.value = 'onetime';

        const subscribeRadio = document.querySelector('input[name="selling_plan"][value="subscription"]');
        const oneTimeRadio = document.querySelector('input[name="selling_plan"][value=""]');

        if (subscribeRadio) subscribeRadio.checked = false;
        if (oneTimeRadio) oneTimeRadio.checked = true;

        this.updateVisualStates(oneTimeRadio);
      }
    }
  }

  resetSubscriptionSelection() {
    const subscribeRadio = document.querySelector('input[name="selling_plan"][value="subscription"]');
    const oneTimeRadio = document.querySelector('input[name="selling_plan"][value=""]');
    const pricingTypeInput = document.querySelector('.pricing-type-input');
    const sellingPlanInput = document.querySelector('.selling-plan-input');

    if (oneTimeRadio) {
      oneTimeRadio.checked = true;
    }

    if (subscribeRadio) {
      subscribeRadio.checked = false;
    }

    if (pricingTypeInput) {
      pricingTypeInput.value = 'onetime';
    }

    if (sellingPlanInput) {
      sellingPlanInput.value = '';
    }

    this.updateVisualStates(oneTimeRadio);
  }

  updatePricesInElement(container, variant) {
    const oneTimePrice = container.querySelector('.custom-one-time-option .custom-subscription-amount .money');
    const oneTimeComparePrice = container.querySelector(
      '.custom-one-time-option .custom-subscription-amount-compare-at-price .money'
    );
    const subscribePrice = container.querySelector('.custom-subscription-option .custom-subscription-amount .money');
    const subscribeComparePrice = container.querySelector(
      '.custom-subscription-option .custom-subscription-compare-amount .money'
    );
    const saveBadge = container.querySelector('.custom-save-badge');

    if (oneTimePrice) {
      oneTimePrice.innerHTML = this.formatMoney(variant.price);
    }

    if (oneTimeComparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        oneTimeComparePrice.innerHTML = this.formatMoney(variant.compare_at_price);
        oneTimeComparePrice.style.display = 'inline';
      } else {
        oneTimeComparePrice.style.display = 'none';
      }
    }

    if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
      const firstSubscription = variant.selling_plan_allocations[0];
      const originalPrice = variant.compare_at_price || variant.price;
      const subscribePriceValue = firstSubscription.per_delivery_price;
      const discountAmount = originalPrice - subscribePriceValue;
      const discountPercentage = Math.round((discountAmount * 100.0) / originalPrice);

      if (subscribePrice) {
        subscribePrice.innerHTML = this.formatMoney(subscribePriceValue);
      }

      if (subscribeComparePrice) {
        subscribeComparePrice.innerHTML = this.formatMoney(originalPrice);
      }

      if (saveBadge) {
        saveBadge.textContent = `SAVE ${discountPercentage}% TODAY`;
      }

      this.updateSubscriptionDetails(variant, firstSubscription);
    } else {
      this.hideSubscriptionOption();
    }

    this.setupSubscriptionListeners();
  }

  updateSubscriptionDetails(variant, firstSubscription) {
    const subscriptionOption = document.querySelector('.custom-subscription-option');
    if (!subscriptionOption) return;

    const frequencySelect = subscriptionOption.querySelector('#custom_selling_plan_select');
    if (frequencySelect) {
      frequencySelect.innerHTML = '';

      variant.selling_plan_allocations.forEach((allocation, index) => {
        const option = document.createElement('option');
        option.value = allocation.selling_plan_id;
        option.dataset.price = allocation.price;
        option.dataset.perDelivery = allocation.per_delivery_price;

        let daysText = '30 Days';
        if (allocation.selling_plan && allocation.selling_plan.name) {
          const planName = allocation.selling_plan.name;
          if (planName.includes('30')) {
            daysText = '30 Days';
          } else if (planName.includes('60')) {
            daysText = '60 Days';
          } else if (planName.includes('90')) {
            daysText = '90 Days';
          } else {
            daysText = planName;
          }
        } else {
          const daysMap = ['30 Days', '60 Days', '90 Days'];
          daysText = daysMap[index] || `${(index + 1) * 30} Days`;
        }

        option.textContent = daysText;
        frequencySelect.appendChild(option);
      });
    }

    const refillText = subscriptionOption.querySelector('.custom-subscribe-option-texts li:first-child');
    if (refillText) {
      const daysText = frequencySelect.options[0]?.textContent || '30 Days';
      refillText.innerHTML = `${this.formatMoney(
        firstSubscription.per_delivery_price
      )} refill every <span class="days">${daysText}</span>`;
    }

    subscriptionOption.style.display = 'block';
  }

  hideSubscriptionOption() {
    const subscriptionOption = document.querySelector('.custom-subscription-option');
    if (subscriptionOption) {
      subscriptionOption.style.display = 'none';
    }
  }

  updateDaysTextFromSelect(select) {
    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption) {
      const daysText = selectedOption.textContent;
      const daysElement = document.querySelector('.custom-subscribe-option-texts .days');

      if (daysElement && daysText) {
        daysElement.textContent = daysText;
      }
    }
  }
}
document.addEventListener('DOMContentLoaded', () => {
  new CustomPriceUpdater();
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomPriceUpdater();
  });
} else {
  new CustomPriceUpdater();
}
